import { Link, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import { cn } from '@/lib/cn';
import { useBrand, useBrandDocument } from '@/lib/brand';
import { DeliverTo } from '@/components/ui/DeliverTo';
import { SECTOR_CATEGORIES, useSector, useSectorInfo } from '@/lib/sector';
import { Icon } from '@/components/icons/Icon';
import { BottomNav } from '@/components/ui/BottomNav';
import { navFor, type NavItem } from '@/components/ui/navItems';
import { useCartCount } from '@/lib/cart';
import { useSession } from '@/lib/session';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAlerts } from '@/lib/useAlerts';
import { SideRail } from '@/components/ui/SideRail';
import { SectorCrossing } from '@/components/ui/SectorSwitch';
import { OfflineBanner } from '@/components/ui/ErrorState';
import { SiteFooter } from './SiteFooter';
import { AnnouncementBanner } from './AnnouncementBanner';

/**
 * The buyer shell.
 *
 * §11 responsive rules, applied literally:
 *   mobile   393px, single column, bottom tab bar, actions in the bottom third
 *   tablet   768–1279, the tab bar becomes a left rail
 *   desktop  1280+, a real header with categories, location, account and cart
 *
 * "A tablet is not a stretched phone" — so the navigation genuinely changes
 * shape at each breakpoint rather than the same bar growing wider.
 *
 * ── AND IT IS TWO SHOPS, NOT ONE ────────────────────────────────────────────
 *
 * The same shell serves human medicines and animal health, in one brand and one
 * colour. `data-sector` on the root is a hook for tests and for anything that
 * needs to know where it is — it no longer changes how anything looks.
 *
 * What tells somebody which shop they are in is words, every one of which is
 * legible on a cracked screen in bright sun: the wordmark reads "ChiCare Vet",
 * a strip across the top says nothing here is for people, every card carries
 * its species, and the switch below the header is always within reach.
 */
export function BuyerLayout() {
  // The uploaded favicon, app icon and brand name into the document. Here
  // rather than in index.html, because that file is built once and the whole
  // point of the setting is that changing the icon is not a deploy.
  useBrandDocument();

  const isOffline = useOfflineStatus();
  const { pathname } = useLocation();
  const sector = useSector();

  // The door between the shops is for people who are shopping. See
  // hidesSectorDoor for the two places it is noise.
  const showsSectorDoor = !hidesSectorDoor(pathname);

  /**
   * ── THE FOOTER IS A HOME-PAGE THING NOW ───────────────────────────
   *
   * It is four columns of links, a search box, the social row, the counterfeit
   * line and the company registration — roughly a phone screen and a half of
   * it. Under the home shelf that is a sitemap somebody can browse. Under a
   * checkout form, a prescription upload or a half-filled address it is a wall
   * of unrelated links between the buyer and the bottom of what they were
   * doing, and every one of them leads away.
   *
   * Both shelves count as home: `/` and `/vet`. Nothing else shows it.
   *
   * What the footer uniquely carried — Help, the returns policy, and now
   * Support — is reachable from the account screen and the bottom nav on every
   * screen, so removing it strands nobody.
   */
  const isHome = pathname === '/' || pathname === '/vet';

  /**
   * ── THE COUNTS THAT FOLLOW A BUYER AROUND ─────────────────────────
   *
   * NavItem has carried a `badge` field all along, and BottomNav and SideRail
   * have both drawn it all along. Nothing ever set it — so the field existed,
   * the markup existed, and no buyer ever saw a number.
   *
   * The bell only appears on the home screen, which means a buyer in checkout,
   * on an order, or reading an article had no way of knowing anything had
   * happened. The tab bar is on every one of those screens.
   */
  const nav = useNavWithCounts(sector);

  // A buyer who taps into a product and back should land where they were, but
  // a NEW route should start at the top. Browsers restore scroll on both.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div data-sector={sector} className="flex min-h-dvh flex-col bg-paper">
      {isOffline ? <OfflineBanner /> : null}

      {/* Admin-controlled, from the settings console. */}
      <AnnouncementBanner />

      <DesktopHeader />

      {/* No switch bar here any more. On a phone the shop toggle is a 44px
          icon in the app bar's brand row (see AppBar), which costs no vertical
          space; at 1280+ the desktop header carries the crossing in words. */}

      <div className="flex flex-1">
        <SideRail items={nav} className="xl:hidden" />

        <div className="flex min-w-0 flex-1 flex-col">
          <main className="flex-1">
            <Outlet />
          </main>
          {showsSectorDoor ? <SectorDoor /> : null}
          {isHome ? <SiteFooter /> : null}
        </div>
      </div>

      <BottomNav items={nav} />
    </div>
  );
}

/**
 * The tab bar, with live numbers on it.
 *
 * ── WHICH ITEM CARRIES WHICH ───────────────────────────────────────────────
 *
 * Cart gets the number of things in it, which needs no account and is the one
 * count people check constantly.
 *
 * Orders carries unread notifications. Almost everything ChiCare notifies
 * about is an order moving — accepted, dispatched, at the door, refunded — so
 * Orders is where somebody goes when they see a number. Putting it on Account
 * would be truthful and useless: it would send them to a settings screen to
 * find out their delivery had arrived.
 *
 * ── AND WHY IT ONLY ASKS WHEN SIGNED IN ────────────────────────────────────
 *
 * A signed-out visitor has no notifications and the endpoint would 401 every
 * twenty seconds for as long as they browsed.
 */
function useNavWithCounts(sector: 'human' | 'veterinary'): NavItem[] {
  const cart = useCartCount();
  // `isUnauthenticated` is only true once we KNOW nobody is signed in, so
  // this stays false while the session is still loading — which is right:
  // better a moment without a badge than a 401 on every poll.
  const { isUnauthenticated } = useSession();
  const signedIn = !isUnauthenticated;

  const { data } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: ({ signal }) => api.unreadNotificationCount(signal),
    enabled: signedIn,
    refetchInterval: 20_000,
    // Nobody is looking at a backgrounded tab, and a phone in a pocket should
    // not spend the hour polling.
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  const unread = data ?? 0;

  // Sound and the tab title. The bell does this too, but it is only on the
  // home screen — this is the copy that covers everywhere else.
  useAlerts(unread, signedIn);

  return useMemo(
    () =>
      navFor(sector).map((item) => {
        if (item.to === '/cart' && cart > 0) return { ...item, badge: cart };
        if (item.to === '/orders' && unread > 0) return { ...item, badge: unread };

        return item;
      }),
    [sector, cart, unread],
  );
}

/**
 * ── THE STICKY BLUE STRIP THAT USED TO BE HERE ──────────────────────────────
 *
 * A dark bar pinned to the top of every veterinary screen, reading "CHICARE VET
 * — For animals only, nothing here is for human use."
 *
 * It was the THIRD copy of that sentence on the same screen. The home screen
 * says it under the Veterinary heading, search says it above the results, and a
 * veterinary product page carries "Animal use only" in a panel above the
 * dosage, where it does the most work.
 *
 * A warning repeated three times in four hundred pixels is not three times the
 * warning. It is a thing people learn to scroll past, and the strip was the
 * easiest of the three to stop seeing: a thin bar at the very top, in the place
 * every app puts chrome.
 *
 * What is left says it once per screen, at full size, in context — which is
 * what §Sector asks for and what the product page has always done.
 */


/**
 * Where the door between the shops belongs, and where it is noise.
 *
 * It exists to tell somebody who is SHOPPING that the shop is twice the size
 * they thought. Under a sign-in form it is telling that to somebody who is not
 * shopping — they are signing in, and "Veterinary medicines and equipment, for
 * livestock and pets" is the last sentence they need before typing an email.
 *
 * Matched on a prefix so /account/addresses and /sign-in?new=1 are covered by
 * the same two entries, rather than each new sub-route having to remember.
 */
function hidesSectorDoor(pathname: string): boolean {
  return ['/account', '/sign-in'].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

/**
 * The door between the two shops, at the end of the page.
 *
 * ── WHY THIS SURVIVED THE SWITCHER ──────────────────────────────────────────
 *
 * The switch under the header does the navigating; this does the explaining.
 * A pill reading "Animal health" tells somebody already looking for it where
 * to press. It does not tell a person who has never thought about it what is
 * behind the door — and that person is most of the traffic, arriving on one
 * product page from a search engine and leaving from the bottom of it.
 *
 * So the switcher is short and always in reach, and this is a sentence, at the
 * end of the scroll, where somebody who has finished reading has a moment to
 * be told the shop is twice the size they thought.
 */
function SectorDoor() {
  const info = useSectorInfo();

  return (
    <nav
      aria-label="Other ChiCare shops"
      data-testid="sector-door"
      className="border-t border-line bg-white px-4 py-4 xl:px-6"
    >
      <Link
        to={info.otherHome}
        className="mx-auto flex min-h-11 w-full max-w-[1200px] items-center gap-3 no-underline"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-bold text-ink">{info.otherLabel}</span>
          <span className="block text-[12px] leading-snug text-muted">{info.otherBlurb}</span>
        </span>
        <Icon name="chevron" size={16} strokeWidth={2} className="shrink-0 text-blue" />
      </Link>
    </nav>
  );
}

/**
 * §11 desktop: "A real header appears — categories, location, account, cart."
 * Hidden below 1280px, where the rail and the bottom bar do this job.
 */
function DesktopHeader() {
  const sector = useSector();
  const info = useSectorInfo();
  const isVet = sector === 'veterinary';

  return (
    <header data-testid="desktop-header" className="hidden border-b border-line bg-white xl:block">
      {/* The trust bar. It is the first thing on the page because it is the
          first question a Nigerian buyer of medicine asks — and on the
          veterinary side the answer names a different regulator, because a
          shop selling cattle antibiotics is licensed by the VCN and saying
          "PCN" there would be a promise we are not making. */}
      <div className="bg-blue-deep py-[7px] text-white">
        <div className="mx-auto flex max-w-[1200px] items-center gap-5 px-6 text-[11.5px] text-white/75">
          <span className="flex items-center gap-1.5">
            {isVet
              ? 'Every seller VCN-registered and verified'
              : 'Every pharmacy PCN-licensed and verified'}
          </span>
          <span className="ml-auto flex gap-5">
            <Link to={info.otherHome} className="text-white/75 no-underline hover:text-white">
              {info.otherLabel}
            </Link>
            {/* These read as navigation, so they have to BE navigation. Text
                styled like a link that does nothing when clicked teaches people
                the header is decorative, and then they stop trying the parts
                that do work. */}
            <Link
              to={isVet ? '/sell?sector=veterinary' : '/sell'}
              className="text-white/75 no-underline hover:text-white"
            >
              {isVet ? 'Sell veterinary on ChiCare' : 'Sell on ChiCare'}
            </Link>
            <Link to="/pages/help" className="text-white/75 no-underline hover:text-white">
              Help
            </Link>
            <Link to="/orders" className="text-white/75 no-underline hover:text-white">
              Track order
            </Link>
          </span>
        </div>
      </div>

      <div className="mx-auto flex max-w-[1200px] items-center gap-[22px] px-6 py-3.5">
        <Link
          to={info.home}
          className="flex items-center gap-1.5 font-display text-[21px] font-extrabold tracking-[-0.02em] text-blue no-underline"
        >
          <span className="grid h-7 w-7 place-items-center rounded-[7px] bg-blue">
            <Logo className="h-4 w-4 fill-white" />
          </span>
          ChiCare
          {info.wordmark ? (
            <span className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-blue">
              {info.wordmark}
            </span>
          ) : null}
        </Link>

        <Link
          to={`${info.home === '/' ? '' : info.home}/search`}
          className="flex min-h-11 max-w-[560px] flex-1 items-center gap-[9px] rounded-[10px] border border-line bg-white px-[13px] text-[13px] text-faint no-underline hover:border-blue"
        >
          <Icon name="search" size={17} strokeWidth={1.9} className="text-muted" />
          {info.searchPlaceholder}
        </Link>

        {/* Was "Wuse II, Abuja", hardcoded, shown to every buyer in the
            country. A buyer in Kano read a delivery address that was not
            theirs on the first screen of a pharmacy app. */}
        <div className="shrink-0">
          <DeliverTo compact />
        </div>

        {/* No bell: there is no notifications screen to send anybody to, and
            an icon that does nothing is worse than an icon that is absent. It
            comes back when the feature does. */}
        <HeaderIcon to="/account" name="user" label="Account" />
        <HeaderIcon to="/cart" name="cart" label="Cart" />
      </div>

      <nav
        aria-label="Categories"
        className="mx-auto flex max-w-[1200px] items-center gap-5 px-6 pb-[11px] text-[12.5px] font-medium text-muted"
      >
        <span className="flex items-center gap-1.5 font-bold text-blue">
          <Icon name="grid" size={14} strokeWidth={2} />
          All categories
        </span>
        {SECTOR_CATEGORIES[sector].map(([label, slug]) => (
          <Link
            key={slug}
            to={`${isVet ? '/vet' : ''}/search?category=${slug}`}
            className="no-underline hover:text-blue"
          >
            {label}
          </Link>
        ))}

        {/* The blog, after the shelves and before the crossing.
            Deliberately UNDECORATED: it had a rule down its left and a mark
            beside the word, both of which were there to say "this is not a
            category". Neither was doing that job — together they made one word
            the busiest thing in the bar, which reads as importance rather than
            as difference. Its position says enough.

            It stays on this side of the wall: an article on poultry withdrawal
            periods has no business among human medicines. */}
        <Link
          to={`${isVet ? '/vet' : ''}/blog`}
          className="no-underline hover:text-blue"
        >
          Blog
        </Link>

        {/* The other half of the shop, at the end of this side's shelves —
            the one place on the header where somebody is already reading a
            list of places to go. */}
        <SectorCrossing className="ml-auto" />
      </nav>
    </header>
  );
}

/**
 * A header icon that actually goes somewhere.
 *
 * It was a `<button>` with no handler, which meant Account and Cart were
 * unreachable from the desktop header entirely — at 1280px and above the rail
 * and the bottom bar are both hidden, so this header IS the navigation, and
 * two thirds of it did nothing.
 */
function HeaderIcon({
  to,
  name,
  label,
  dot = false,
}: {
  to: string;
  name: 'bell' | 'user' | 'cart';
  label: string;
  dot?: boolean;
}) {
  return (
    <Link
      to={to}
      aria-label={label}
      className="relative grid h-11 w-11 shrink-0 place-items-center rounded-[9px] text-muted no-underline hover:bg-paper hover:text-ink"
    >
      <Icon name={name} size={18} strokeWidth={1.7} />
      {dot ? (
        <span className="absolute right-[5px] top-[5px] h-[7px] w-[7px] rounded-full border-[1.5px] border-white bg-danger" />
      ) : null}
    </Link>
  );
}

/**
 * The brand mark.
 *
 * ── WHY THIS READS A SETTING ──────────────────────────────────────
 *
 * Settings → Brand has had a logo field for months, and uploading one changed
 * nothing visible: the footer was the only place that read it, and all eight
 * headers drew this cross. Somebody uploading their logo saw the default and
 * reasonably concluded the upload had failed.
 *
 * Changed HERE rather than at the eight call sites, so a ninth header added
 * next month gets the right mark without anybody remembering to.
 *
 * ── WHY THE SVG STAYS ───────────────────────────────────────────
 *
 * It is the fallback, and it has to be: these marks sit inside a small blue
 * tile on a sign-in screen, and an app that renders a blank box where its own
 * name should be looks broken in exactly the place trust matters most.
 *
 * The uploaded file is rendered white-on-blue in those tiles, which suits a
 * mark with transparency. `logo_dark_id` is the one to use on deep backgrounds
 * when an admin has supplied it.
 */
export function Logo({ className }: { className?: string }) {
  const { logo, logoDark, name } = useBrand();
  const uploaded = logoDark ?? logo;

  if (uploaded?.url) {
    return (
      <img
        src={uploaded.url}
        alt={uploaded.alt ?? name}
        // object-contain, never cover: a logo cropped to fill a square is a
        // logo with its edges cut off, and nobody uploads one expecting that.
        className={cn('object-contain', className)}
      />
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden className={cn('fill-current', className)}>
      <path d="M10 3h4v5h5v4h-5v5h-4v-5H5V8h5z" />
    </svg>
  );
}

/** §11 fifth state. Nigerian networks drop out mid-scroll; the app says so. */
function useOfflineStatus(): boolean {
  const [offline, setOffline] = useState(() => !navigator.onLine);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);

    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  return offline;
}
